import moment from 'moment';
import 'moment/locale/pt'; // Import supported locales
import 'moment/locale/tet';
import { Document, Page, Text, View, PDFDownloadLink, Font, Image } from '@react-pdf/renderer';
import { getContractData } from '../../types/procurement-types';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

// Register Inter font from Google Fonts
Font.register({
  family: 'Inter',
  fonts: [
    { src: `${process.env.PUBLIC_URL}/assets/fonts/Inter_18pt-Regular.ttf` },
    { src: `${process.env.PUBLIC_URL}/assets/fonts/Inter_18pt-Medium.ttf`, fontWeight: 500 },
    { src: `${process.env.PUBLIC_URL}/assets/fonts/Inter_18pt-SemiBold.ttf`, fontWeight: 600 },
  ],
});

interface PDFDocProps {
  singleContract: getContractData;
}

const PDFDoc: React.FC<PDFDocProps> = ({ singleContract }) => {
  const totalAmount = singleContract.paymentTerms?.reduce(
    (sum, paymentTerm) => sum + (paymentTerm?.amount || 0),
    0
  );

  return (
    <Document>
      <Page size="A4" style={{ padding: 48, fontFamily: 'Inter' }}>
        <View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 15 }}>
            <View style={{ width: '35%' }}>
              <Image
                src={`${process.env.PUBLIC_URL}/assets/images/anp-logo.png`}
                style={{ width: 143, height: 72 }}
              />
            </View>

            <View style={{ color: '#000', width: '65%' }}>
              <Text style={{ fontWeight: 600 }}>Contract Offer</Text>
              <Text>{singleContract.clientName}</Text>
              <Text style={{ fontSize: 14, color: '#808080' }}>
                <Text style={{ fontWeight: 600 }}>No.</Text> 0050 |{' '}
                <Text style={{ fontWeight: 600 }}>Date</Text>{' '}
                {moment(Date.now()).format('DD. MM. YYYY')}
              </Text>
            </View>
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 30 }}>
            <View style={{ width: '35%' }}>
              <Image
                src={`${process.env.PUBLIC_URL}/assets/images/nacional-logo.png`}
                style={{ width: 143, height: 72 }}
              />
            </View>

            <View style={{ flexDirection: 'row', width: '65%' }}>
              <View style={{ color: '#000', width: '55%' }}>
                <Text style={{ fontWeight: 500, marginBottom: 10 }}>From</Text>
                <Text style={{ fontSize: 12, fontWeight: 600 }}>Your Name</Text>
                <Text style={{ fontSize: 12 }}>Adress here</Text>
                <Text style={{ fontSize: 12 }}>Zipcode & city</Text>
                <Text style={{ fontSize: 12 }}>name@email.com</Text>
              </View>

              <View style={{ color: '#000', width: '45%' }}>
                <Text style={{ fontWeight: 500, marginBottom: 10 }}>To</Text>
                <Text style={{ fontSize: 12, fontWeight: 600 }}>{singleContract.clientEmail}</Text>
                <Text style={{ fontSize: 12 }}>Adress here</Text>
                <Text style={{ fontSize: 12 }}>Zipcode & city</Text>
              </View>
            </View>
          </View>

          <Text style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: '#101828' }}>
            Offer Description
          </Text>
          <Text style={{ fontSize: 10, marginBottom: 20, width: '80%' }}>
            {singleContract.description}
          </Text>

          <View style={{ width: '60%', marginBottom: 20 }}>
            <Text style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Deliverables</Text>

            <View
              style={{
                flexDirection: 'row',
                color: '#525252',
                borderBottom: 1,
                borderBottomColor: '#E5E5E5',
                paddingVertical: 8,
                paddingRight: 16,
              }}
            >
              <Text style={{ fontSize: 10, width: '40%', fontWeight: 500 }}>Milestone</Text>
              <Text style={{ fontSize: 10, width: '40%', fontWeight: 500 }}>Due date</Text>
              <Text style={{ fontSize: 10, width: '20%', fontWeight: 500 }}>Amount</Text>
            </View>

            {singleContract?.contractDeliverables?.map((deliverable) => (
              <View
                key={deliverable.deliverableId}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  color: '#101828',
                  borderBottom: 1,
                  borderBottomColor: '#E5E5E5',
                  paddingVertical: 8,
                  paddingRight: 16,
                }}
              >
                <Text style={{ fontSize: 10, width: '40%', fontWeight: 500 }}>
                  {deliverable?.milestone}
                </Text>
                <Text style={{ fontSize: 10, width: '40%', fontWeight: 500 }}>
                  {moment(deliverable?.dueDate).format('MMM DD, YYYY')}
                </Text>
                <Text style={{ fontSize: 10, width: '20%', fontWeight: 500 }}>
                  ${new Intl.NumberFormat('en-US').format(deliverable?.amount)}
                </Text>
              </View>
            ))}

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                color: '#101828',
                paddingVertical: 8,
                paddingRight: 16,
              }}
            >
              <Text style={{ fontSize: 10, width: '40%', fontWeight: 500 }}>Second review</Text>
              <Text style={{ fontSize: 10, width: '40%', fontWeight: 500 }}>Nov 10, 2024</Text>
              <Text style={{ fontSize: 10, width: '20%', fontWeight: 500 }}>$1,200</Text>
            </View>
          </View>

          <View style={{ width: '60%', marginBottom: 20 }}>
            <Text style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Payment Terms</Text>

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                color: '#525252',
                borderBottom: 1,
                borderBottomColor: '#E5E5E5',
                paddingVertical: 8,
                paddingRight: 16,
              }}
            >
              <View style={{ width: '80%' }}>
                <Text style={{ fontSize: 10, fontWeight: 600, color: '#101828' }}>Offer</Text>
                <Text style={{ fontSize: 10, fontWeight: 500 }}>
                  Total amount for this opportunity
                </Text>
              </View>

              <Text style={{ fontSize: 10, width: '20%', fontWeight: 500 }}>N/A</Text>
            </View>

            {singleContract.paymentTerms?.map((paymentTerm, index) => (
              <View
                key={paymentTerm.description + index}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  color: '#101828',
                  borderBottom: 1,
                  borderBottomColor: '#E5E5E5',
                  paddingVertical: 8,
                  paddingRight: 16,
                }}
              >
                <Text style={{ fontSize: 10, width: '80%', fontWeight: 600, color: '#101828' }}>
                  {paymentTerm.description}
                </Text>
                <Text style={{ fontSize: 10, width: '20%', fontWeight: 500 }}>
                  ${new Intl.NumberFormat('en-US').format(paymentTerm?.amount)}
                </Text>
              </View>
            ))}

            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                color: '#525252',
                borderBottom: 1,
                borderBottomColor: '#E5E5E5',
                paddingVertical: 8,
                paddingRight: 16,
              }}
            >
              <View style={{ width: '80%' }}>
                <Text style={{ fontSize: 10, fontWeight: 600, color: '#101828' }}>
                  You will get
                </Text>
                <Text style={{ fontSize: 10, width: '80%', fontWeight: 500 }}>
                  The estimated amount you will receive after service fees.
                </Text>
              </View>

              <Text style={{ fontSize: 10, width: '20%', fontWeight: 500 }}>
                ${new Intl.NumberFormat('en-US').format(totalAmount)}
              </Text>
            </View>
          </View>
        </View>
      </Page>

      <Page size="A4" style={{ fontFamily: 'Inter' }}>
        <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'space-between' }}>
          <View style={{ padding: 48 }}>
            <View style={{ flexDirection: 'row', marginBottom: 30 }}>
              <Image
                src={`${process.env.PUBLIC_URL}/assets/images/anp-logo.png`}
                style={{ width: 143, height: 72 }}
              />
              <Image
                src={`${process.env.PUBLIC_URL}/assets/images/nacional-logo.png`}
                style={{ width: 143, height: 72, marginLeft: 20 }}
              />
            </View>

            <View style={{ width: '60%', marginBottom: 30 }}>
              <Text style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
                Contract details
              </Text>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  color: '#101828',
                  borderBottom: 1,
                  borderBottomColor: '#E5E5E5',
                  paddingVertical: 8,
                  paddingRight: 16,
                }}
              >
                <Text style={{ fontSize: 10, width: '45%', fontWeight: 500, color: '#101828' }}>
                  Total Amount
                </Text>
                <Text style={{ fontSize: 10, width: '55%', fontWeight: 500 }}>
                  {singleContract.amount}
                </Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  color: '#101828',
                  borderBottom: 1,
                  borderBottomColor: '#E5E5E5',
                  paddingVertical: 8,
                  paddingRight: 16,
                }}
              >
                <Text style={{ fontSize: 10, width: '45%', fontWeight: 500, color: '#101828' }}>
                  Category
                </Text>
                <Text style={{ fontSize: 10, width: '55%', fontWeight: 500 }}>N/A</Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  color: '#101828',
                  borderBottom: 1,
                  borderBottomColor: '#E5E5E5',
                  paddingVertical: 8,
                  paddingRight: 16,
                }}
              >
                <Text style={{ fontSize: 10, width: '45%', fontWeight: 500, color: '#101828' }}>
                  Offer date
                </Text>
                <Text style={{ fontSize: 10, width: '55%', fontWeight: 500 }}>
                  {moment(singleContract.offerDate).format('MMM DD, YYYY')}
                </Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  color: '#101828',
                  borderBottom: 1,
                  borderBottomColor: '#E5E5E5',
                  paddingVertical: 8,
                  paddingRight: 16,
                }}
              >
                <Text style={{ fontSize: 10, width: '45%', fontWeight: 500, color: '#101828' }}>
                  Offer expires
                </Text>
                <Text style={{ fontSize: 10, width: '55%', fontWeight: 500 }}>
                  {moment(singleContract.offerExpire).format('MMM DD, YYYY')}
                </Text>
              </View>
            </View>

            <View style={{ width: '60%', marginBottom: 30 }}>
              <Text style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
                Provide Payment Details
              </Text>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  color: '#101828',
                  borderBottom: 1,
                  borderBottomColor: '#E5E5E5',
                  paddingVertical: 8,
                  paddingRight: 16,
                }}
              >
                <Text style={{ fontSize: 10, fontWeight: 500, color: '#101828' }}>Bank Name</Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  color: '#101828',
                  borderBottom: 1,
                  borderBottomColor: '#E5E5E5',
                  paddingVertical: 8,
                  paddingRight: 16,
                }}
              >
                <Text style={{ fontSize: 10, fontWeight: 500, color: '#101828' }}>Account No</Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  color: '#101828',
                  borderBottom: 1,
                  borderBottomColor: '#E5E5E5',
                  paddingVertical: 8,
                  paddingRight: 16,
                }}
              >
                <Text style={{ fontSize: 10, fontWeight: 500, color: '#101828' }}>Beneficiary</Text>
              </View>
            </View>
          </View>

          <View style={{ backgroundColor: '#F5F5F5', width: '100%', padding: 48 }}>
            <View style={{ width: '90%' }}>
              <View style={{ flexDirection: 'row', alignItems: 'flex-end', marginBottom: 10 }}>
                <Text style={{ fontSize: 10, color: '#101828' }}>Submitted by:</Text>
                <View
                  style={{
                    borderBottom: 1,
                    borderBottomColor: '#E5E5E5',
                    width: '70%',
                    height: 1,
                    marginLeft: 10,
                  }}
                ></View>
              </View>

              <Text style={{ fontSize: 10, marginBottom: 20 }}>
                In witness whereof, the parties hereto have executed this Agreement as of the day
                and year first below written.
              </Text>

              <View style={{ flexDirection: 'row', paddingBottom: 30 }}>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '40%',
                    color: '#101828',
                    borderTop: 1,
                    borderTopColor: '#E5E5E5',
                    paddingVertical: 8,
                    paddingRight: 16,
                  }}
                >
                  <Text style={{ fontSize: 10, color: '#101828' }}>Client Name</Text>
                </View>

                <View style={{ width: '40%' }}></View>

                <View
                  style={{
                    flexDirection: 'row',
                    width: '20%',
                    justifyContent: 'space-between',
                    color: '#101828',
                    borderTop: 1,
                    borderTopColor: '#E5E5E5',
                    paddingVertical: 8,
                    paddingRight: 16,
                  }}
                >
                  <Text style={{ fontSize: 10, color: '#101828' }}>Date</Text>
                </View>
              </View>

              <View style={{ flexDirection: 'row' }}>
                <View
                  style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '40%',
                    color: '#101828',
                    borderTop: 1,
                    borderTopColor: '#E5E5E5',
                    paddingVertical: 8,
                    paddingRight: 16,
                  }}
                >
                  <Text style={{ fontSize: 10, color: '#101828' }}>
                    Authorised Supplier Signature
                  </Text>
                </View>

                <View style={{ width: '40%' }}></View>

                <View
                  style={{
                    flexDirection: 'row',
                    width: '20%',
                    justifyContent: 'space-between',
                    color: '#101828',
                    borderTop: 1,
                    borderTopColor: '#E5E5E5',
                    paddingVertical: 8,
                    paddingRight: 16,
                  }}
                >
                  <Text style={{ fontSize: 10, color: '#101828' }}>Date</Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

interface OverviewComponentProps {
  singleContract: getContractData;
}

const OverviewComponent: React.FC<OverviewComponentProps> = ({ singleContract }) => {
  const { t, i18n } = useTranslation();

  // Set moment.js locale whenever the language changes
  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  const totalAmount = singleContract.paymentTerms?.reduce(
    (sum, paymentTerm) => sum + (paymentTerm?.amount || 0),
    0
  );

  return (
    <>
      <div className="mb-[35px] mt-4 grid items-center gap-y-6 border-y border-[#EDE8E7] pb-6 pt-4 xl:grid-cols-[1fr_1fr]">
        <div className="flex flex-col gap-y-2">
          <span className="text-sm text-[#525252]">Contract Start date:</span>
          <span className="text-base font-medium text-[#161616]">N/A</span>
          {/* <span className="text-base font-medium text-[#161616]">28/08/2024</span> */}
        </div>

        <div className="grid grid-cols-[1.5fr_1fr]">
          <div className="flex flex-col gap-y-2">
            <span className="text-sm text-[#525252]">Contract End date:</span>
            <span className="text-base font-medium text-[#161616]">N/A</span>
            {/* <span className="text-base font-medium text-[#161616]">31/10/2024</span> */}
          </div>

          <div className="flex flex-col gap-y-2">
            <span className="text-sm text-[#525252]">Current status:</span>
            <span className="text-sm font-medium text-[#24A148]">N/A</span>
            {/* <span className="text-sm font-medium text-[#24A148]">Active</span> */}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-y-12 xl:w-3/5">
        <div className="flex flex-col gap-y-2">
          <h3 className="text-xl font-semibold text-[#101828]">Offer Description</h3>
          <p className="text-sm text-[#101828]">{singleContract?.description}</p>

          {/* <Link
            to=""
            className="text-sm font-semibold text-[#24A148] underline"
            target="_blank"
            rel="noreferrer noopener"
          >
            View original job post
          </Link> */}
        </div>

        <div className="flex flex-col gap-y-2">
          <h3 className="text-xl font-semibold text-[#101828]">Attachment</h3>
          <div className="flex items-center gap-x-6">
            <div className="w-full rounded-[5px] bg-[#F5F5F5] py-2.5">Contract-Offer.pdf</div>

            {/* <button className="cursor-pointer px-3 py-1.5 font-semibold text-[#24A148] underline">
              Download
            </button> */}

            <PDFDownloadLink
              document={<PDFDoc singleContract={singleContract} />}
              fileName="Contract-Offer.pdf"
              className="cursor-pointer px-3 py-1.5 font-semibold text-[#24A148] underline"
            >
              {({ loading }) => <>{loading ? 'Generating Doc...' : 'Download'}</>}
            </PDFDownloadLink>
          </div>
        </div>

        <div>
          <h3 className="mb-6 text-xl font-semibold text-[#101828]">Deliverables</h3>
          <div>
            <div className="mb-4 grid grid-cols-[1fr_1fr_.5fr] border-b border-[#E5E5E5] pb-4 text-sm font-medium text-[#525252]">
              <span>{t('milestone', 'Milestone')}</span>
              <span>{t('dueDate', 'Due date')}</span>
              <span>{t('amount', 'Amount')}</span>
            </div>

            {singleContract?.contractDeliverables?.map((deliverable) => (
              <div
                key={deliverable.deliverableId}
                className="mb-4 grid grid-cols-[1fr_1fr_.5fr] border-b border-[#E5E5E5] pb-4 text-sm font-medium text-[#101828]"
              >
                <span>{deliverable?.milestone}</span>
                <span>{moment(deliverable?.dueDate).format('MMM DD, YYYY')}</span>
                <span>${new Intl.NumberFormat('en-US').format(deliverable?.amount)}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="mb-6 text-xl font-semibold text-[#101828]">Payment Terms</h3>

          <div>
            <div className="mb-4 grid grid-cols-[2fr_.5fr] border-b border-[#E5E5E5] pb-4">
              <div>
                <span className="text-base font-semibold text-[#101828]">Offer</span>
                <p className="text-sm font-medium text-[#525252]">
                  Total amount for this opportunity
                </p>
              </div>

              <span>N/A</span>
              {/* <span>$62,000</span> */}
            </div>
          </div>

          {singleContract.paymentTerms?.map((paymentTerm, index) => (
            <div key={paymentTerm.description + index}>
              <div className="mb-4 grid grid-cols-[2fr_.5fr] border-b border-[#E5E5E5] pb-4">
                <span className="text-base font-semibold text-[#101828]">
                  {paymentTerm.description}
                </span>

                <span>${new Intl.NumberFormat('en-US').format(paymentTerm?.amount)}</span>
              </div>
            </div>
          ))}

          <div>
            <div className="mb-4 grid grid-cols-[2fr_.5fr] border-b border-[#E5E5E5] pb-4">
              <div>
                <span className="text-base font-semibold text-[#101828]">You will get</span>
                <p className="mt-5 max-w-[326px] text-sm font-medium text-[#525252]">
                  The estimated amount you will receive after service fees.
                </p>
              </div>

              <span>${new Intl.NumberFormat('en-US').format(totalAmount)}</span>
              {/* <span>$51,460</span> */}
            </div>
          </div>
        </div>

        <div>
          <h3 className="mb-6 text-xl font-semibold text-[#101828]">Contract details</h3>

          <div className="mb-4 grid grid-cols-[1.5fr_2fr] border-b border-[#E5E5E5] pb-4 text-sm font-medium">
            <span className="text-[#525252]">Total Amount</span>
            <span className="text-[#101828]">
              ${new Intl.NumberFormat('en-US').format(singleContract?.amount ?? 0)}
            </span>
          </div>

          <div className="mb-4 grid grid-cols-[1.5fr_2fr] border-b border-[#E5E5E5] pb-4 text-sm font-medium">
            <span className="text-[#525252]">Category</span>
            <span className="text-[#101828]">N/A</span>
          </div>

          <div className="mb-4 grid grid-cols-[1.5fr_2fr] border-b border-[#E5E5E5] pb-4 text-sm font-medium">
            <span className="text-[#525252]">Offer Date</span>
            <span className="text-[#101828]">
              {moment(singleContract?.offerDate).format('MMM DD, YYYY')}
            </span>
          </div>

          <div className="mb-4 grid grid-cols-[1.5fr_2fr] border-b border-[#E5E5E5] pb-4 text-sm font-medium">
            <span className="text-[#525252]">Offer expires</span>
            <span className="text-[#101828]">
              {moment(singleContract?.offerExpire).format('MMM DD, YYYY')}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default OverviewComponent;
